// The Research view (design §8): the advisor's understanding, in full.
//
// This is transparency, not a task list. What it believes and what it read,
// then the comments it has closed, then what all of it cost.
//
// The "open questions" section is gone (D27). A note is now a claim, and a
// claim with nothing behind it is not a note — a thing only the author can
// answer is a comment of type `question`, which says why it matters and what
// an answer would look like, and lives with the paragraph it is about.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../api/client";
import { hostOf, when } from "../advisor/CommentCard";
import { NoteCard } from "../advisor/NoteCard";
import { type AdvisorState, closed, standing } from "../advisor/types";
import { useDocument } from "./documentContext";

interface Props {
  state: AdvisorState;
  usage: {
    by_kind: Record<string, { runs: number; cost_usd: number }>;
    total_cost_usd: number;
  } | null;
  onGoToParagraph: (id: string) => void;
  onDismissNote: (slug: string) => void;
}

export function Research({ state, usage, onGoToParagraph, onDismissNote }: Props) {
  const notes = standing(state.notes);
  const archive = closed(state.comments);
  const [showAuto, setShowAuto] = useState(false);

  const shown = showAuto ? archive : archive.filter((c) => c.dismiss_reason !== "orphaned");

  return (
    <div className="research">
      <section>
        <h2>What it understands</h2>
        {notes.length === 0 && (
          <p className="muted small">
            Nothing yet. It reads while you write, and researches as the text grows.
          </p>
        )}
        {notes.map((note) => (
          <NoteCard
            key={note.slug}
            note={note}
            onDismiss={() => onDismissNote(note.slug)}
            onGoToParagraph={onGoToParagraph}
          />
        ))}
      </section>

      <section>
        <h2>Sources</h2>
        {state.sources.length === 0 && <p className="muted small">None read yet.</p>}
        <ul className="plain">
          {state.sources.map((s) => {
            const resting = state.notes.filter((n) => n.sources.some((x) => x.id === s.id)).length;
            return (
              <li key={s.id}>
                <a href={s.url} target="_blank" rel="noreferrer noopener">
                  {hostOf(s.url)}
                </a>
                <span className="muted small">
                  {" "}
                  · read {when(s.retrieved_at)} · {resting} note{resting === 1 ? "" : "s"} rest on
                  it
                </span>
                {s.stale && <span className="stale"> may be out of date</span>}
              </li>
            );
          })}
        </ul>
      </section>

      <section>
        <h2>Archive</h2>
        {shown.length === 0 && <p className="muted small">Nothing closed yet.</p>}
        <ul className="plain">
          {shown.map((c) => (
            <li key={c.slug}>
              <span className="muted small">
                {c.dismiss_reason === "addressed"
                  ? "Addressed"
                  : c.state === "resolved"
                    ? "Resolved"
                    : "Dismissed"}
                {c.dismiss_reason === "orphaned" ? " (its paragraph was deleted)" : ""} ·{" "}
                {c.closed_at ? when(c.closed_at) : ""}
              </span>
              <p>{c.what}</p>
            </li>
          ))}
        </ul>
        {archive.some((c) => c.dismiss_reason === "orphaned") && (
          <button
            type="button"
            className="text-button flush"
            onClick={() => setShowAuto((v) => !v)}
          >
            {showAuto ? "Hide" : "Show"} the ones closed automatically
          </button>
        )}
      </section>

      <section>
        <h2>Usage</h2>
        {!usage || usage.total_cost_usd === 0 ? (
          <p className="muted small">Nothing spent on this document yet.</p>
        ) : (
          <table className="usage">
            <tbody>
              {Object.entries(usage.by_kind).map(([kind, row]) => (
                <tr key={kind}>
                  <td>{kind}</td>
                  <td className="muted">
                    {row.runs} run{row.runs === 1 ? "" : "s"}
                  </td>
                  <td className="num">${row.cost_usd.toFixed(3)}</td>
                </tr>
              ))}
              <tr className="total">
                <td>total</td>
                <td />
                <td className="num">${usage.total_cost_usd.toFixed(3)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </section>

      {/* The log used to have a button of its own in the dock. It is one step
          deeper now, where the rest of what the advisor did is kept: the same
          account of the same work, read at a different grain (design §12.2). */}
      <section>
        <h2>What it has done</h2>
        <Link to="log" className="text-button">
          Every run, and what it read
        </Link>
      </section>
    </div>
  );
}

type Usage = Props["usage"];

/** Research as a route under the document (design §14.4).
 *
 *  It asks for the usage itself. Its own mount is the only condition that
 *  ever mattered — the screen used to hold this and guard it with a boolean
 *  saying whether this panel was showing. */
export function ResearchRoute() {
  const { documentId, advisor, advisorBump, onDismissNote, onGoToParagraph } = useDocument();
  const [usage, setUsage] = useState<Usage>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<NonNullable<Usage>>(`/api/documents/${documentId}/usage`)
      .then((u) => {
        if (!cancelled) setUsage(u);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [documentId, advisorBump]);

  return (
    <Research
      state={advisor.state}
      usage={usage}
      onDismissNote={onDismissNote}
      onGoToParagraph={onGoToParagraph}
    />
  );
}
